/**
 * §7.4 rendering rules, as a component.
 *
 * absent      -> renders nothing (rule 2: never render a control the actor cannot
 *                use; never render-then-403).
 * unavailable -> renders the control disabled, with the server's reason next to
 *                it (rule 3: an advertised capability is never silently hidden).
 * granted     -> renders enabled.
 *
 * The visible difference between "absent" and "disabled" is the point: absent
 * means never granted, disabled means temporarily unavailable.
 */

import type { ReactNode } from 'react';
import type { Capability } from '@pm/shared';
import type { CapabilityResolver } from '../hooks/useCapabilities';

export function Gate({
  caps,
  capability,
  children,
  /** Rendered in place of nothing when the actor should be told the gap exists. */
  fallback,
}: {
  caps: CapabilityResolver;
  capability: Capability;
  children: (disabled: boolean, reason: string | null) => ReactNode;
  fallback?: ReactNode;
}) {
  const state = caps.of(capability);
  if (state.state === 'absent') return <>{fallback ?? null}</>;

  const disabled = state.state === 'unavailable';
  const reason = disabled ? state.reason : null;

  return (
    <div className={`gate${disabled ? ' gate-unavailable' : ''}`}>
      {children(disabled, reason)}
      {reason ? (
        <p className="gate-reason" role="status">
          Temporarily unavailable: {reason}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The listing's advertised permissions rendered as a legend, so a renter can see
 * which of them are live, which are blocked and why, and which the listing never
 * included. Without this the three states collapse into "the button isn't there".
 */
export function CapabilityLegend({
  caps,
  advertised,
}: {
  caps: CapabilityResolver;
  advertised: Capability[];
}) {
  if (advertised.length === 0) return null;
  return (
    <ul className="cap-legend">
      {advertised.map((c) => {
        const s = caps.of(c);
        const tone = s.state === 'granted' ? 'ok' : s.state === 'unavailable' ? 'warn' : 'neutral';
        return (
          <li key={c} className={`cap-legend-item cap-${tone}`}>
            <span className="cap-name">{c.replace(/_/g, ' ')}</span>
            <span className="cap-state">
              {s.state === 'granted'
                ? 'available'
                : s.state === 'unavailable'
                  ? `unavailable — ${s.reason}`
                  : 'not part of this rental'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
